import Moment from 'moment';

import { FetchAPI, ParamsSerializer } from './auth-service';

const ExportTypes = [
    { title: 'CSV', key: 'csv', type: 'text/plain' },
    { title: 'HTML', key: 'html', type: 'text/html' },
    { title: 'Plain text', key: 'rst', type: 'text/plain' },
    // ['JSON', 'json', 'application/json'],
];

const reportsFields = [
    {
        variable: 'title',
        hidden: false,
        headerName: 'Title',
        field: 'data.title',
        defaultSort: 'asc',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'type',
        hidden: false,
        headerName: 'Type',
        field: 'data.id',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'schedule',
        hidden: false,
        headerName: 'Schedule',
        field: 'data.frequency',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'formats',
        hidden: false,
        headerName: 'Formats',
        field: 'data.attachments',
        type: 'string',
        sortable: false,
    },
    {
        variable: 'recipients',
        hidden: false,
        headerName: 'Recipients',
        field: 'data.recipients',
        type: 'string',
        sortable: false,
    },
];

const historyFields = [
    {
        variable: 'created',
        hidden: false,
        headerName: 'Created',
        field: 'created',
        defaultSort: 'asc',
        type: 'date',
    },
    {
        variable: 'finished',
        hidden: false,
        headerName: 'Finished',
        field: 'finished.Time',
        type: 'date',
    },
    {
        variable: 'status',
        hidden: false,
        headerName: 'Status',
        field: 'data.status',
        type: 'string',
    },
    {
        variable: 'size',
        hidden: false,
        headerName: 'Size',
        field: 'data.size',
        type: 'number',
    },
    {
        variable: 'report',
        hidden: false,
        headerName: 'Report',
        field: 'report_data.title',
        type: 'string',
    },
    {
        variable: 'delivered_to',
        hidden: false,
        headerName: 'Recipients',
        field: 'report_data.recipients',
        type: 'string',
        sortable: false,
    },
];

const NestedSearch = (o, orig, r, search) => {
    search = search || '';
    Object.keys(o).some((x) => {
        if (!o[x]) {
            return false;
        } else if (typeof o[x] === 'object') {
            NestedSearch(o[x], orig, r, search);
        } else if (typeof o[x] === 'string') {
            const m = o[x].toLowerCase().match(search.toLowerCase());
            if (m) r.push(orig);
            return m;
        }
    });
};

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

class ReportsDataService {
    constructor () {
        // we only need one of these
        if (!ReportsDataService.instance) {
            ReportsDataService.instance = this;
        }
        this.searchPlaceholder = 'Search report templates';
        this.fields = reportsFields;

        this.getReports = (search) => {
            return FetchAPI({ method: 'GET', url: 'system/reports' })
                .then(res => {
                    if (!res) res = { reports: [] };
                    self.asof = res.timestamp;
                    this.reports = res.reports || [];
                    return {
                        data: res.reports || [],
                        page: 1,
                        totalCount: this.total || (res.reports || []).length,
                    };
                });
        };

        this.getSavedSearches = () => {
            try {
                let searches = localStorage.getItem('savedSearches_reports') || '[]';
                if (searches.length < 1) searches = '[]';
                return JSON.parse(searches);
            } catch (err) {
                console.log('getSavedSearches error', err);
                return [];
            }
        };

        this.saveSearch = (str) => {
            if (!str || !str.length) return;
            const searches = this.getSavedSearches();
            const i = searches.findIndex(s => s === str);
            if (i >= 0) searches.splice(i, 1);
            searches.unshift(str);
            searches.splice(5);
            localStorage.setItem('savedSearches_reports', JSON.stringify(searches));
        };

        this.runReport = (uuid) => {
            return FetchAPI({
                method: 'POST',
                url: `system/reports/${uuid}/-run`,
            });
        };

        this.downloadReport = () => {
            return FetchAPI({
                method: 'POST',
                url: 'system/-export',
            });
        };

        this.createReport = (data) => {
            return FetchAPI({
                method: 'POST',
                url: 'system/reports',
                body: data,
            });
        };

        this.updateReport = (uuid, data) => {
            return FetchAPI({
                method: 'PUT',
                url: `system/reports/${uuid}`,
                body: data,
            });
        };

        this.deleteReport = (uuid) => {
            return FetchAPI({
                method: 'DELETE',
                url: `system/reports/${uuid}`,
            });
        };

        this.getReportConstants = () => {
            return FetchAPI({
                method: 'GET',
                url: 'system/reports/-constants',
            });
        };

        this.getReportMaxRows = () => {
            return FetchAPI({
                method: 'GET',
                url: 'system/reports/settings/',
                cache: true,
            });
        };

        this.downloadAlertsReport = () => {
            return FetchAPI({
                method: 'POST',
                url: 'system/reports/alerts/',
            });
        };

        this.downloadAuditReport = () => {
            return FetchAPI({
                method: 'POST',
                url: 'system/reports/audit/',
            });
        };

        this.downloadMetadataReport = () => {
            return FetchAPI({
                method: 'POST',
                url: 'system/reports/metadata/',
            });
        };

        this.downloadSuricataReport = () => {
            return FetchAPI({
                method: 'POST',
                url: 'system/reports/suricy/',
            });
        };

        this.downloadDataReport = (type, params, columns, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'POST',
                url: `${MC2CHECK ? `mc2/cmcapi/${cmc}/` : ''}system/reports/${type}/?${ParamsSerializer(params)}`,
                body: columns,
            });
        };

        return ReportsDataService.instance;
    }
}

class HistoryDataService {
    constructor () {
        // we only need one of these
        if (!HistoryDataService.instance) {
            HistoryDataService.instance = this;
        }
        this.searchPlaceholder = 'Search report history';
        this.fields = reportsFields;

        this.getHistory = (search) => {
            return FetchAPI({ method: 'GET', url: 'system/reports' })
                .then(res => {
                    if (!res) res = { history: [] };
                    self.asof = res.timestamp;
                    return {
                        data: res.history || [],
                        page: 1,
                        totalCount: this.total || (res.history || []).length,
                    };
                });
        };

        this.getSavedSearches = () => {
            try {
                let searches = localStorage.getItem('savedSearches_reports_history') || '[]';
                if (searches.length < 1) searches = '[]';
                return JSON.parse(searches);
            } catch (err) {
                console.log('getSavedSearches error', err);
                return [];
            }
        };

        this.saveSearch = (str) => {
            if (!str || !str.length) return;
            const searches = this.getSavedSearches();
            const i = searches.findIndex(s => s === str);
            if (i >= 0) searches.splice(i, 1);
            searches.unshift(str);
            searches.splice(5);
            localStorage.setItem('savedSearches_reports_history', JSON.stringify(searches));
        };

        this.deleteAllHistory = () => {
            return FetchAPI({
                method: 'DELETE',
                url: 'system/reports/history/-all',
            });
        };

        this.deleteSomeHistory = uuids => {
            return FetchAPI({
                method: 'DELETE',
                url: 'system/reports/history/-some',
                body: uuids,
            });
        };

        this.deleteHistory = (uuid, seq) => {
            return FetchAPI({
                method: 'DELETE',
                url: `system/reports/${uuid}/history/${seq}`,
            });
        };

        this.downloadReportFromHistory = (uuid, seq, format) => {
            return FetchAPI({
                method: 'POST',
                url: `system/reports/${uuid}/history/${seq}/${format}/-download`,
            }).then((res) => {
                let f = '';
                switch (format) {
                    case 'pdf':
                        f = 'application/pdf';
                        break;
                    case 'csv':
                        f = 'text/csv';
                        break;
                    case 'html':
                        f = 'text/html';
                        break;
                    default:
                        f = 'text/plain';
                        break;
                }

                return URL.createObjectURL(new Blob([res], { type: `${f}` }), `report-${Moment().format('MM/DD/YY-HH:mm:ss')}.${format === 'rst' ? 'txt' : format}`);
            });
        };

        return HistoryDataService.instance;
    }
}

const ReportsService = new ReportsDataService();
const HistoryService = new HistoryDataService();

export {
    ReportsService as default,
    HistoryService,
    reportsFields,
    historyFields,
    ExportTypes,
    NestedSearch,
};
